/**
 * Every visible string and link on the logged-out landing page.
 *
 * Deliberately one flat, typed object rather than copy inlined into components:
 * the i18n follow-up feeds this straight through `localizeLandingConfig`, and
 * the English strings here become the catalog keys in
 * src/i18n/generated/landing-page-locales.json. Inlining copy into JSX is what
 * makes that migration a rewrite instead of a mapping.
 */

import {
  MARKETING_FOOTER_ROLE_SLUGS,
  MARKETING_ROLES,
  marketingRoleLinks,
} from './role-page-model';
import {
  MARKETING_INDUSTRIES,
  marketingIndustryLinks,
} from './industry-page-model';
import type {
  MarketingPageComposer,
  MarketingPageCta,
  MarketingPageFaq,
} from './marketing-page-content-types';

export type MarketingNavLink = {
  label: string;
  href: string;
};

export type MarketingNavGroup = {
  /** Stable across locales. Use this for logic; `title` is display copy. */
  id?: string;
  title: string;
  links: Array<MarketingNavLink>;
};

export const MARKETING_PRIMARY_NAV: Array<MarketingNavLink> = [
  { label: 'Solutions', href: '#solutions' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Contact', href: '/contact' },
];

const MARKETING_INDUSTRY_LINKS = marketingIndustryLinks(MARKETING_INDUSTRIES);

export const MARKETING_SOLUTIONS_GROUPS: Array<MarketingNavGroup> = [
  {
    title: 'For your Role',
    links: marketingRoleLinks(MARKETING_ROLES.map((role) => role.slug)),
  },
  {
    title: 'For your Industry',
    links: MARKETING_INDUSTRY_LINKS,
  },
];

export const MARKETING_BRAND = {
  name: 'IMAI Studio',
  tagline: "Your company's agentic Chief of Staff",
  loginLabel: 'Login',
  loginHref: '/auth',
  ctaLabel: 'Start Creating',
  ctaHref: '/auth?mode=signup',
  openMenuLabel: 'Open menu',
  closeMenuLabel: 'Close menu',
  primaryNavLabel: 'Primary',
  footerNavLabel: 'Footer',
} as const;

export const MARKETING_HERO = {
  titleLines: ['Build Powerful Brands', '& Products Fast'],
  subtitle: 'The learning curve should belong to the AI',
} as const;

export const MARKETING_COMPOSER: MarketingPageComposer = {
  placeholder: 'Build what you wish existed',
  useCasesLabel: 'Use cases',
  addLabel: 'Add context',
  voiceLabel: 'Use your voice',
  sendLabel: 'Send message',
  accessibleLabel: 'Start creating with IMAI',
  href: '/auth?mode=signup',
  menuItems: [
    {
      id: 'upload',
      label: 'Upload files',
      description: 'Images, docs, audio, video for context',
    },
    {
      id: 'references',
      label: 'References',
      description: 'Add product, style, and color context',
    },
    {
      id: 'tools',
      label: 'Tools',
      description: 'Clean, transform, inspect, and export existing assets',
    },
    {
      id: 'connectors',
      label: 'Connectors',
      description: 'Bring your tools into the conversation.',
    },
    {
      id: 'skills',
      label: 'Skills',
      description: 'Create a reusable workflow with AI',
    },
  ],
  /** Keep chip labels short while filling the composer with a complete brief. */
  examples: [
    {
      label: "Generate next Season's US Trend Report",
      prompt:
        'Create a trend report for the next season in the US market. Ask me about my product category, target customer, and season, then research key silhouettes, colors, materials, prints, and consumer shifts. Cite recent sources, distinguish emerging signals from established trends, and recommend how my brand can apply the findings to its next collection.',
    },
    {
      label: 'Build a 65 SKU collection for European market',
      prompt:
        'Help me plan a 65 SKU collection for the European market. Ask about my brand, target customer, season, product categories, and retail price range. Propose a balanced assortment with styles, colorways, sizes, materials, and price points. Define how SKUs are counted, make the total exactly 65, and explain the commercial role of each group.',
    },
    {
      label: 'Estimate FOB Shanghai cost for my product',
      prompt:
        'Estimate the per-unit FOB Shanghai cost of my product. Ask for the design, materials, construction details, order quantity, packaging, and currency. Break down materials, trims, labor, finishing, packaging, inland transport, and export handling. Show assumptions and a cost range, flag missing inputs, and exclude ocean freight, import duties, and destination delivery.',
    },
    {
      label: 'Create & schedule Instagram campaign',
      prompt:
        'Develop an Instagram launch campaign for my product. Ask about the product, target audience, campaign goal, brand voice, and launch date. Create a campaign concept and content calendar with Reels, carousels, captions, opening hooks, calls to action, and visual briefs. Include an asset checklist and a proposed posting schedule for my approval.',
    },
    {
      label: 'Design with latest trends, colors & materials',
      prompt:
        'Help me design a product using current trends, colors, and materials. Ask about the category, target customer, season, brand references, and price target. Research relevant signals with sources, then propose three distinct design directions with silhouettes, palettes, materials, and trims. Explain brand fit, manufacturing feasibility, and cost tradeoffs for each direction.',
    },
    {
      label: 'Convert design into Tech Pack',
      prompt:
        'Turn my design and reference images into a detailed tech pack for manufacturing. Ask for missing product specifications and size requirements. Include front and back technical flats, a bill of materials, measurements, tolerances, grading, construction and seam details, trims, colorways, labels, packaging, and quality checks. Clearly flag specifications that still need my confirmation.',
    },
    {
      label: 'Create sustainable packaging for Footwear',
      prompt:
        'Develop sustainable packaging for my footwear product. Ask about shoe dimensions, protection needs, order volume, brand style, budget, and sales market. Compare material-efficient, recyclable options for cost, durability, and shipping. Recommend box dimensions, materials, dieline notes, print finishes, and disposal instructions, and flag environmental claims that need verification.',
    },
    {
      label: 'Generate launch video for my hoodie',
      prompt:
        'Plan a launch video lasting 15 to 30 seconds for my hoodie using my product images and brand references. Ask about the audience, platform, and mood. Create a script and storyboard with an opening hook, fabric and fit details, styling shots, on-screen text, music direction, and a closing call to action. Include shot prompts and platform-specific formats without inventing product details.',
    },
  ],
};

export const MARKETING_LOGOS = {
  heading: 'Trusted by teams at',
  /** Proper names double as stable keys for the exact exported logo vectors. */
  brands: [
    // 'Michael Kors', // Hidden for now (Raj, 2026-10-20). Uncomment to restore its existing logo.
    'Anli',
    'Cantu',
    'Kitex',
    'Start-Rite',
    // 'Brandix', // Uncomment to restore its existing logo.
  ],
} as const;

export type MarketingShowcase = {
  id: string;
  label: string;
  heading: string;
  subheading: string;
  mediaAlt: string;
  placeholderMedia: MarketingShowcaseMedia;
};

export type MarketingShowcaseMediaAsset = {
  url: string;
  type: 'image' | 'video';
  label?: string;
  title?: string;
  description?: string;
};

export type MarketingShowcaseMedia = {
  mode: 'image' | 'video' | 'carousel';
  assets: Array<MarketingShowcaseMediaAsset>;
};

export const MARKETING_SHOWCASE_CONTROLS = {
  previous: 'Previous',
  next: 'Next',
} as const;

export const MARKETING_SHOWCASES: Array<MarketingShowcase> = [
  {
    id: 'design',
    label: 'Taste with Design',
    heading: 'Turn your Chaos into a Beautiful Collection',
    subheading: 'On trend. On cost. On time.',
    mediaAlt: 'A product rendered from a design brief',
    placeholderMedia: {
      mode: 'image',
      assets: [],
    },
  },
  {
    id: 'manufacture',
    label: 'Manufacture with Ease',
    heading: 'Convert Designs into Tech Packs',
    subheading: 'Generate everything a factory needs to make it.',
    mediaAlt: 'A technical specification generated from a design',
    placeholderMedia: {
      mode: 'image',
      assets: [],
    },
  },
  {
    id: 'connectors',
    label: 'Connectors — One Intelligence Layer',
    heading: 'Keep the Tools you Trust',
    subheading: 'Connect Email, CRM, Shopify, Analytics, and more',
    mediaAlt: 'Connected tools feeding a single workspace',
    placeholderMedia: {
      mode: 'image',
      assets: [],
    },
  },
];

export type MarketingFeature = {
  title: string;
  description: string;
};

export const MARKETING_FEATURES = {
  label: 'End-to-End Studio',
  heading: 'Everything in One Place.',
  description:
    'Every tool your team needs to design, spec, cost, and market products.',
  cards: [
    {
      title: 'Control by Voice',
      description: 'Operate the entire studio with a voice conversation.',
    },
    {
      title: 'Trend, Material & Color Forecaster',
      description: 'Increase your sell-through by being on trend.',
    },
    {
      title: 'Cost & Demand Clarity',
      description:
        'See the real landed cost of a design and which pieces are actually selling.',
    },
    {
      title: 'Campaign Ready',
      description:
        'Generate product visuals, video, and UGC-style creative for every channel.',
    },
    {
      title: 'Built for Teams',
      description:
        'Shared drives, comments, and approvals keep every function working from the same files.',
    },
    {
      title: 'Connected to Your Stack',
      description:
        'Plug into Gmail, Calendar, your CRM, Analytics, and Meta Ads without leaving the studio.',
    },
  ] satisfies Array<MarketingFeature>,
} as const;

export type MarketingFaqItem = {
  question: string;
  answer: string;
};

export const MARKETING_FAQ: MarketingPageFaq = {
  label: 'Frequently Asked Questions',
  heading: 'FAQ',
  items: [
    {
      question:
        'Why should I use IMAI instead of Claude, ChatGPT and Gemini / Nano Banana?',
      answer:
        'Instead of producing disconnected outputs, IMAI helps carry your intent from the first idea through collection development and technical documentation.',
    },
    {
      question: 'Who is IMAI for?',
      answer:
        'Turn early ideas and references into cohesive product collections your team can review and refine.',
    },
    {
      question: 'How does IMAI keep designs on-brand, on trend and on cost?',
      answer:
        'IMAI uses your references and creative direction to guide the work, while you remain in control of every choice.',
    },
    {
      question: 'How can IMAI help me with creating my own brand?',
      answer:
        'IMAI supports your team by accelerating exploration and reducing repetitive work—not replacing creative or technical judgment.',
    },
    {
      question:
        'How accurate are your technical specifications for manufacturing?',
      answer:
        'IMAI can turn approved concepts into editable specification drafts for your team to review, refine, and prepare for handoff.',
    },
  ] satisfies Array<MarketingFaqItem>,
};

export const MARKETING_CTA: MarketingPageCta = {
  eyebrow: 'Creative Leaders Using IMAI',
  heading: 'Get started with IMAI now.',
  /**
   * Desktop leads with the project card, mobile leads with the training card.
   * Source order follows mobile; the desktop frame is reproduced with `order-*`
   * so both breakpoints share one DOM tree.
   */
  cards: [
    {
      label: 'Join a free live training session',
      href: '/contact',
    },
    { label: 'Direct your first project', href: '/auth?mode=signup' },
  ],
};

export const MARKETING_FOOTER_GROUPS: Array<MarketingNavGroup> = [
  {
    id: 'role',
    title: 'For your role',
    links: marketingRoleLinks(MARKETING_FOOTER_ROLE_SLUGS),
  },
  {
    id: 'industry',
    title: 'For your industry',
    links: MARKETING_INDUSTRY_LINKS,
  },
  {
    id: 'legal',
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  },
];
